import { Injectable } from '@angular/core';

/**
 * Service pour gérer les champs partagés dans les eForms.
 *
 * Certains champs partagent le même emplacement JSON mais ont des conditions d'affichage différentes.
 * Par exemple :
 * - BT-615-Lot et BT-15-Lot partagent le même jsonPath selon la valeur de BT-14-Lot
 * - BT-615-Part et BT-15-Part partagent le même jsonPath
 *
 * Ces champs ne doivent JAMAIS être vidés ou initialisés automatiquement par les systèmes
 * de validation/conditions car cela viderait l'autre champ qui partage le même emplacement.
 */
@Injectable({
  providedIn: 'root'
})
export class EformSharedFieldsService {

  /**
   * Liste des champs qui partagent le même emplacement JSON.
   * Ces champs doivent être traités de manière spéciale pour éviter les conflits.
   */
  private readonly SHARED_LOCATION_FIELDS = [
    'BT-615-Lot',
    'BT-15-Lot',
    'BT-615-Part',
    'BT-15-Part',
    'BT-140-notice',
    'BT-26(m)-Lot',
    'BT-26(m)-Procedure',
    'BT-26(a)-Lot',
    'BT-26(a)-Procedure',
    'BT-26(m)-Part',
    'BT-26(a)-Part'
  ];

  /**
   * Vérifie si un champ est un champ partagé (partage le même emplacement JSON avec un autre champ).
   *
   * @param fieldId L'identifiant du champ à vérifier
   * @returns true si le champ est un champ partagé, false sinon
   */
  isSharedLocationField(fieldId: string | undefined | null): boolean {
    if (!fieldId) {
      return false;
    }

    return this.SHARED_LOCATION_FIELDS.some(sharedField => fieldId.includes(sharedField));
  }

  /**
   * Ajoute un nouveau champ à la liste des champs partagés.
   * Utile pour étendre dynamiquement la liste si nécessaire.
   *
   * @param fieldId L'identifiant du champ à ajouter
   */
  addSharedLocationField(fieldId: string): void {
    if (!this.SHARED_LOCATION_FIELDS.includes(fieldId)) {
      this.SHARED_LOCATION_FIELDS.push(fieldId);
    }
  }

  /**
   * Retourne la liste complète des champs partagés.
   *
   * @returns Array des identifiants de champs partagés
   */
  getSharedLocationFields(): ReadonlyArray<string> {
    return this.SHARED_LOCATION_FIELDS;
  }
}

